/**
 * Base Item Registry
 *
 * POE-style base item catalog with defense types, grid sizes, and level tiers.
 * Each slot has bases at levels 1, 15, 30 across 3 defense archetypes (AR/EV/ES).
 */

import { EquipmentSlot, DefenseType, WeaponSubType, EquipmentCategory, GridSize, ItemMod } from '@shared/types';

export interface BaseItemDefinition {
  id: string;
  name: string;
  slot: EquipmentSlot;
  category: EquipmentCategory;
  level: number;
  gridSize: GridSize;
  defenseType?: DefenseType;
  weaponSubType?: WeaponSubType;
  implicit: ItemMod[];
  setId?: string;
}

// ========================
// HEAD BASES (9 bases)
// ========================
const HEAD_BASES: BaseItemDefinition[] = [
  // Armour
  { id: 'head_ar_1', name: 'Demir Börk', slot: 'head', category: 'armour', level: 1, gridSize: { width: 1, height: 1 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 12, tier: 1 }] },
  { id: 'head_ar_15', name: 'Çelik Tolga', slot: 'head', category: 'armour', level: 15, gridSize: { width: 1, height: 1 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 28, tier: 2 }] },
  { id: 'head_ar_30', name: 'Tunç Miğfer', slot: 'head', category: 'armour', level: 30, gridSize: { width: 1, height: 1 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 48, tier: 3 }] },
  // Evasion
  { id: 'head_ev_1', name: 'Deri Şapka', slot: 'head', category: 'armour', level: 1, gridSize: { width: 1, height: 1 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 12, tier: 1 }] },
  { id: 'head_ev_15', name: 'Avcı Şapkası', slot: 'head', category: 'armour', level: 15, gridSize: { width: 1, height: 1 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 28, tier: 2 }] },
  { id: 'head_ev_30', name: 'Gölge Kaskı', slot: 'head', category: 'armour', level: 30, gridSize: { width: 1, height: 1 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 48, tier: 3 }] },
  // Energy Shield
  { id: 'head_es_1', name: 'İpek Sargı', slot: 'head', category: 'armour', level: 1, gridSize: { width: 1, height: 1 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 10, tier: 1 }] },
  { id: 'head_es_15', name: 'Runik Başlık', slot: 'head', category: 'armour', level: 15, gridSize: { width: 1, height: 1 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 24, tier: 2 }] },
  { id: 'head_es_30', name: 'Kam Tacı', slot: 'head', category: 'armour', level: 30, gridSize: { width: 1, height: 1 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 42, tier: 3 }] },
];

// ========================
// CHEST BASES (9 bases, gridSize 2x3)
// ========================
const CHEST_BASES: BaseItemDefinition[] = [
  // Armour
  { id: 'chest_ar_1', name: 'Demir Zırh', slot: 'chest', category: 'armour', level: 1, gridSize: { width: 2, height: 3 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 25, tier: 1 }] },
  { id: 'chest_ar_15', name: 'Çelik Zırh', slot: 'chest', category: 'armour', level: 15, gridSize: { width: 2, height: 3 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 55, tier: 2 }] },
  { id: 'chest_ar_30', name: 'Ağır Plaka', slot: 'chest', category: 'armour', level: 30, gridSize: { width: 2, height: 3 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 90, tier: 3 }] },
  // Evasion
  { id: 'chest_ev_1', name: 'Deri Zırh', slot: 'chest', category: 'armour', level: 1, gridSize: { width: 2, height: 3 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 25, tier: 1 }] },
  { id: 'chest_ev_15', name: 'Sert Deri', slot: 'chest', category: 'armour', level: 15, gridSize: { width: 2, height: 3 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 55, tier: 2 }] },
  { id: 'chest_ev_30', name: 'Gölge Zırhı', slot: 'chest', category: 'armour', level: 30, gridSize: { width: 2, height: 3 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 90, tier: 3 }] },
  // Energy Shield
  { id: 'chest_es_1', name: 'Kumaşlı Kaftan', slot: 'chest', category: 'armour', level: 1, gridSize: { width: 2, height: 3 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 20, tier: 1 }] },
  { id: 'chest_es_15', name: 'İpek Cübbe', slot: 'chest', category: 'armour', level: 15, gridSize: { width: 2, height: 3 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 45, tier: 2 }] },
  { id: 'chest_es_30', name: 'Runik Cübbe', slot: 'chest', category: 'armour', level: 30, gridSize: { width: 2, height: 3 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 75, tier: 3 }] },
];

// ========================
// GLOVES BASES (9 bases, gridSize 1x2)
// ========================
const GLOVES_BASES: BaseItemDefinition[] = [
  // Armour
  { id: 'gloves_ar_1', name: 'Demir Eldiven', slot: 'gloves', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 8, tier: 1 }] },
  { id: 'gloves_ar_15', name: 'Çelik Eldiven', slot: 'gloves', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 18, tier: 2 }] },
  { id: 'gloves_ar_30', name: 'Savaş Eldiveni', slot: 'gloves', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 32, tier: 3 }] },
  // Evasion
  { id: 'gloves_ev_1', name: 'Deri Eldiven', slot: 'gloves', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 8, tier: 1 }] },
  { id: 'gloves_ev_15', name: 'Avcı Eldiveni', slot: 'gloves', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 18, tier: 2 }] },
  { id: 'gloves_ev_30', name: 'Gölge Eldiveni', slot: 'gloves', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 32, tier: 3 }] },
  // Energy Shield
  { id: 'gloves_es_1', name: 'İpek Eldiven', slot: 'gloves', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 6, tier: 1 }] },
  { id: 'gloves_es_15', name: 'Runik Eldiven', slot: 'gloves', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 15, tier: 2 }] },
  { id: 'gloves_es_30', name: 'Kam Eldiveni', slot: 'gloves', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 28, tier: 3 }] },
];

// ========================
// BOOTS BASES (9 bases, gridSize 1x2)
// ========================
const BOOTS_BASES: BaseItemDefinition[] = [
  // Armour
  { id: 'boots_ar_1', name: 'Demir Çizme', slot: 'boots', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 8, tier: 1 }, { type: 'moveSpeed', value: 5, tier: 1 }] },
  { id: 'boots_ar_15', name: 'Çelik Çizme', slot: 'boots', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 18, tier: 2 }, { type: 'moveSpeed', value: 5, tier: 1 }] },
  { id: 'boots_ar_30', name: 'Savaş Çizmesi', slot: 'boots', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 32, tier: 3 }, { type: 'moveSpeed', value: 5, tier: 1 }] },
  // Evasion
  { id: 'boots_ev_1', name: 'Deri Çizme', slot: 'boots', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 8, tier: 1 }, { type: 'moveSpeed', value: 10, tier: 1 }] },
  { id: 'boots_ev_15', name: 'Hafif Çizme', slot: 'boots', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 18, tier: 2 }, { type: 'moveSpeed', value: 10, tier: 1 }] },
  { id: 'boots_ev_30', name: 'Gölge Çizmesi', slot: 'boots', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 32, tier: 3 }, { type: 'moveSpeed', value: 10, tier: 1 }] },
  // Energy Shield
  { id: 'boots_es_1', name: 'İpek Terlik', slot: 'boots', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 6, tier: 1 }, { type: 'moveSpeed', value: 5, tier: 1 }] },
  { id: 'boots_es_15', name: 'Runik Çizme', slot: 'boots', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 15, tier: 2 }, { type: 'moveSpeed', value: 5, tier: 1 }] },
  { id: 'boots_es_30', name: 'Kam Çizmesi', slot: 'boots', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 28, tier: 3 }, { type: 'moveSpeed', value: 5, tier: 1 }] },
];

// ========================
// BELT BASES (6 bases, gridSize 1x1)
// ========================
const BELT_BASES: BaseItemDefinition[] = [
  // Armour
  { id: 'belt_ar_1', name: 'Deri Kemer', slot: 'belt', category: 'armour', level: 1, gridSize: { width: 1, height: 1 }, defenseType: 'armour', implicit: [{ type: 'maxHp', value: 15, tier: 1 }] },
  { id: 'belt_ar_15', name: 'Çelik Kemer', slot: 'belt', category: 'armour', level: 15, gridSize: { width: 1, height: 1 }, defenseType: 'armour', implicit: [{ type: 'maxHp', value: 30, tier: 2 }] },
  { id: 'belt_ar_30', name: 'Savaş Kemeri', slot: 'belt', category: 'armour', level: 30, gridSize: { width: 1, height: 1 }, defenseType: 'armour', implicit: [{ type: 'maxHp', value: 50, tier: 3 }] },
  // Evasion
  { id: 'belt_ev_1', name: 'Hafif Kemer', slot: 'belt', category: 'armour', level: 1, gridSize: { width: 1, height: 1 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 8, tier: 1 }] },
  { id: 'belt_ev_15', name: 'Avcı Kemeri', slot: 'belt', category: 'armour', level: 15, gridSize: { width: 1, height: 1 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 18, tier: 2 }] },
  { id: 'belt_ev_30', name: 'Gölge Kemeri', slot: 'belt', category: 'armour', level: 30, gridSize: { width: 1, height: 1 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 32, tier: 3 }] },
  // Energy Shield
  { id: 'belt_es_1', name: 'Ruh Kemeri', slot: 'belt', category: 'armour', level: 1, gridSize: { width: 1, height: 1 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 10, tier: 1 }] },
  { id: 'belt_es_15', name: 'Mistik Kemer', slot: 'belt', category: 'armour', level: 15, gridSize: { width: 1, height: 1 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 22, tier: 2 }] },
  { id: 'belt_es_30', name: 'Büyülü Kemer', slot: 'belt', category: 'armour', level: 30, gridSize: { width: 1, height: 1 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 40, tier: 3 }] },
];

// ========================
// WEAPON BASES (15+ bases)
// ========================
const WEAPON_BASES: BaseItemDefinition[] = [
  // Swords (1x3)
  { id: 'weapon_sword_1', name: 'Kılıç', slot: 'weapon', category: 'weapon', level: 1, gridSize: { width: 1, height: 3 }, weaponSubType: 'sword', implicit: [{ type: 'damage', value: 10, tier: 1 }] },
  { id: 'weapon_sword_15', name: 'Uzun Kılıç', slot: 'weapon', category: 'weapon', level: 15, gridSize: { width: 1, height: 3 }, weaponSubType: 'sword', implicit: [{ type: 'damage', value: 25, tier: 2 }] },
  { id: 'weapon_sword_30', name: 'Tuğrul Kılıcı', slot: 'weapon', category: 'weapon', level: 30, gridSize: { width: 1, height: 3 }, weaponSubType: 'sword', implicit: [{ type: 'damage', value: 45, tier: 3 }] },
  // Axes (1x3)
  { id: 'weapon_axe_1', name: 'Balta', slot: 'weapon', category: 'weapon', level: 1, gridSize: { width: 1, height: 3 }, weaponSubType: 'axe', implicit: [{ type: 'damage', value: 12, tier: 1 }] },
  { id: 'weapon_axe_15', name: 'Savaş Baltası', slot: 'weapon', category: 'weapon', level: 15, gridSize: { width: 1, height: 3 }, weaponSubType: 'axe', implicit: [{ type: 'damage', value: 30, tier: 2 }, { type: 'critChance', value: 5, tier: 1 }] },
  { id: 'weapon_axe_30', name: 'Celladın Baltası', slot: 'weapon', category: 'weapon', level: 30, gridSize: { width: 1, height: 3 }, weaponSubType: 'axe', implicit: [{ type: 'damage', value: 52, tier: 3 }, { type: 'critChance', value: 8, tier: 2 }] },
  // Bows (2x4)
  { id: 'weapon_bow_1', name: 'Yay', slot: 'weapon', category: 'weapon', level: 1, gridSize: { width: 2, height: 4 }, weaponSubType: 'bow', implicit: [{ type: 'damage', value: 8, tier: 1 }, { type: 'attackSpeed', value: 10, tier: 1, calcType: 'increased' }] },
  { id: 'weapon_bow_15', name: 'Kompozit Yay', slot: 'weapon', category: 'weapon', level: 15, gridSize: { width: 2, height: 4 }, weaponSubType: 'bow', implicit: [{ type: 'damage', value: 20, tier: 2 }, { type: 'attackSpeed', value: 15, tier: 2, calcType: 'increased' }] },
  { id: 'weapon_bow_30', name: 'Bozkır Yayı', slot: 'weapon', category: 'weapon', level: 30, gridSize: { width: 2, height: 4 }, weaponSubType: 'bow', implicit: [{ type: 'damage', value: 38, tier: 3 }, { type: 'attackSpeed', value: 20, tier: 3, calcType: 'increased' }] },
  // Staves (1x4)
  { id: 'weapon_staff_1', name: 'Asa', slot: 'weapon', category: 'weapon', level: 1, gridSize: { width: 1, height: 4 }, weaponSubType: 'staff', implicit: [{ type: 'spellDamage', value: 15, tier: 1, calcType: 'increased' }] },
  { id: 'weapon_staff_15', name: 'Kam Asası', slot: 'weapon', category: 'weapon', level: 15, gridSize: { width: 1, height: 4 }, weaponSubType: 'staff', implicit: [{ type: 'spellDamage', value: 30, tier: 2, calcType: 'increased' }] },
  { id: 'weapon_staff_30', name: 'Runik Asa', slot: 'weapon', category: 'weapon', level: 30, gridSize: { width: 1, height: 4 }, weaponSubType: 'staff', implicit: [{ type: 'spellDamage', value: 50, tier: 3, calcType: 'increased' }] },
  // Daggers (1x2)
  { id: 'weapon_dagger_1', name: 'Hançer', slot: 'weapon', category: 'weapon', level: 1, gridSize: { width: 1, height: 2 }, weaponSubType: 'dagger', implicit: [{ type: 'damage', value: 7, tier: 1 }, { type: 'critChance', value: 10, tier: 1, calcType: 'increased' }] },
  { id: 'weapon_dagger_15', name: 'Kama', slot: 'weapon', category: 'weapon', level: 15, gridSize: { width: 1, height: 2 }, weaponSubType: 'dagger', implicit: [{ type: 'damage', value: 16, tier: 2 }, { type: 'critChance', value: 18, tier: 2, calcType: 'increased' }] },
  { id: 'weapon_dagger_30', name: 'Gölge Hançeri', slot: 'weapon', category: 'weapon', level: 30, gridSize: { width: 1, height: 2 }, weaponSubType: 'dagger', implicit: [{ type: 'damage', value: 28, tier: 3 }, { type: 'critChance', value: 28, tier: 3, calcType: 'increased' }] },
  // Wands (1x2)
  { id: 'weapon_wand_1', name: 'Değnek', slot: 'weapon', category: 'weapon', level: 1, gridSize: { width: 1, height: 2 }, weaponSubType: 'wand', implicit: [{ type: 'spellDamage', value: 10, tier: 1, calcType: 'increased' }, { type: 'castSpeed', value: 5, tier: 1, calcType: 'increased' }] },
  { id: 'weapon_wand_15', name: 'Runik Değnek', slot: 'weapon', category: 'weapon', level: 15, gridSize: { width: 1, height: 2 }, weaponSubType: 'wand', implicit: [{ type: 'spellDamage', value: 22, tier: 2, calcType: 'increased' }, { type: 'castSpeed', value: 8, tier: 2, calcType: 'increased' }] },
  { id: 'weapon_wand_30', name: 'Kam Değneği', slot: 'weapon', category: 'weapon', level: 30, gridSize: { width: 1, height: 2 }, weaponSubType: 'wand', implicit: [{ type: 'spellDamage', value: 38, tier: 3, calcType: 'increased' }, { type: 'castSpeed', value: 12, tier: 3, calcType: 'increased' }] },
  // Maces (1x3)
  { id: 'weapon_mace_1', name: 'Topuz', slot: 'weapon', category: 'weapon', level: 1, gridSize: { width: 1, height: 3 }, weaponSubType: 'hammer', implicit: [{ type: 'damage', value: 14, tier: 1 }] },
  { id: 'weapon_mace_15', name: 'Gürz', slot: 'weapon', category: 'weapon', level: 15, gridSize: { width: 1, height: 3 }, weaponSubType: 'hammer', implicit: [{ type: 'damage', value: 34, tier: 2 }] },
  { id: 'weapon_mace_30', name: 'Savaş Gürzü', slot: 'weapon', category: 'weapon', level: 30, gridSize: { width: 1, height: 3 }, weaponSubType: 'hammer', implicit: [{ type: 'damage', value: 58, tier: 3 }] },
];

// ========================
// OFFHAND/SHIELD BASES (6 bases, gridSize 1x3)
// ========================
const OFFHAND_BASES: BaseItemDefinition[] = [
  // Armour shields
  { id: 'offhand_ar_1', name: 'Tahta Kalkan', slot: 'offhand', category: 'armour', level: 1, gridSize: { width: 1, height: 3 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 15, tier: 1 }, { type: 'blockChance', value: 15, tier: 1 }] },
  { id: 'offhand_ar_15', name: 'Demir Kalkan', slot: 'offhand', category: 'armour', level: 15, gridSize: { width: 1, height: 3 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 35, tier: 2 }, { type: 'blockChance', value: 20, tier: 2 }] },
  { id: 'offhand_ar_30', name: 'Kule Kalkanı', slot: 'offhand', category: 'armour', level: 30, gridSize: { width: 1, height: 3 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 60, tier: 3 }, { type: 'blockChance', value: 25, tier: 3 }] },
  // Evasion shields
  { id: 'offhand_ev_1', name: 'Deri Targe', slot: 'offhand', category: 'armour', level: 1, gridSize: { width: 1, height: 3 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 15, tier: 1 }, { type: 'blockChance', value: 10, tier: 1 }] },
  { id: 'offhand_ev_15', name: 'Hafif Kalkan', slot: 'offhand', category: 'armour', level: 15, gridSize: { width: 1, height: 3 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 35, tier: 2 }, { type: 'blockChance', value: 14, tier: 2 }] },
  { id: 'offhand_ev_30', name: 'Gölge Kalkanı', slot: 'offhand', category: 'armour', level: 30, gridSize: { width: 1, height: 3 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 60, tier: 3 }, { type: 'blockChance', value: 18, tier: 3 }] },
  // Energy Shield shields (Spirit Shields)
  { id: 'offhand_es_1', name: 'Ruh Kalkanı', slot: 'offhand', category: 'armour', level: 1, gridSize: { width: 1, height: 3 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 15, tier: 1 }, { type: 'maxMana', value: 10, tier: 1 }] },
  { id: 'offhand_es_15', name: 'Mistik Kalkan', slot: 'offhand', category: 'armour', level: 15, gridSize: { width: 1, height: 3 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 35, tier: 2 }, { type: 'maxMana', value: 20, tier: 2 }] },
  { id: 'offhand_es_30', name: 'Büyücü Kalkanı', slot: 'offhand', category: 'armour', level: 30, gridSize: { width: 1, height: 3 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 60, tier: 3 }, { type: 'maxMana', value: 35, tier: 3 }] },
];

// ========================
// AMULET BASES (3 bases, gridSize 1x1)
// ========================
const AMULET_BASES: BaseItemDefinition[] = [
  { id: 'amulet_1', name: 'Muska', slot: 'amulet', category: 'accessory', level: 1, gridSize: { width: 1, height: 1 }, implicit: [{ type: 'allResist', value: 5, tier: 1 }] },
  { id: 'amulet_15', name: 'Gümüş Kolye', slot: 'amulet', category: 'accessory', level: 15, gridSize: { width: 1, height: 1 }, implicit: [{ type: 'allResist', value: 10, tier: 2 }] },
  { id: 'amulet_30', name: 'Altın Kolye', slot: 'amulet', category: 'accessory', level: 30, gridSize: { width: 1, height: 1 }, implicit: [{ type: 'allResist', value: 16, tier: 3 }] },
];

// ========================
// RING BASES (3 bases, gridSize 1x1)
// ========================
const RING_BASES: BaseItemDefinition[] = [
  { id: 'ring_1', name: 'Yüzük', slot: 'ring1', category: 'accessory', level: 1, gridSize: { width: 1, height: 1 }, implicit: [] },
  { id: 'ring_15', name: 'Gümüş Yüzük', slot: 'ring1', category: 'accessory', level: 15, gridSize: { width: 1, height: 1 }, implicit: [{ type: 'maxHp', value: 15, tier: 1 }] },
  { id: 'ring_30', name: 'Altın Yüzük', slot: 'ring1', category: 'accessory', level: 30, gridSize: { width: 1, height: 1 }, implicit: [{ type: 'maxHp', value: 30, tier: 2 }] },
];

// ========================
// CAPE BASES (6 bases, gridSize 1x2)
// ========================
const CAPE_BASES: BaseItemDefinition[] = [
  // Armour capes
  { id: 'cape_ar_1', name: 'Yün Pelerin', slot: 'cape', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 5, tier: 1 }] },
  { id: 'cape_ar_15', name: 'Savaş Pelerini', slot: 'cape', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 15, tier: 2 }, { type: 'maxHp', value: 20, tier: 1 }] },
  { id: 'cape_ar_30', name: 'Tunç Pelerin', slot: 'cape', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'armour', implicit: [{ type: 'armor', value: 28, tier: 3 }, { type: 'maxHp', value: 35, tier: 2 }] },
  // Evasion capes
  { id: 'cape_ev_1', name: 'İpek Pelerin', slot: 'cape', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 8, tier: 1 }] },
  { id: 'cape_ev_15', name: 'Gölge Pelerini', slot: 'cape', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 20, tier: 2 }] },
  { id: 'cape_ev_30', name: 'Hayalet Pelerini', slot: 'cape', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'evasion', implicit: [{ type: 'evasion', value: 38, tier: 3 }] },
  // Energy Shield capes
  { id: 'cape_es_1', name: 'Ruh Pelerini', slot: 'cape', category: 'armour', level: 1, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 8, tier: 1 }] },
  { id: 'cape_es_15', name: 'Mistik Pelerin', slot: 'cape', category: 'armour', level: 15, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 20, tier: 2 }] },
  { id: 'cape_es_30', name: 'Büyücü Pelerini', slot: 'cape', category: 'armour', level: 30, gridSize: { width: 1, height: 2 }, defenseType: 'energyShield', implicit: [{ type: 'energyShield', value: 38, tier: 3 }] },
];

// ========================
// FULL REGISTRY
// ========================
const ALL_BASES: BaseItemDefinition[] = [
  ...HEAD_BASES,
  ...CHEST_BASES,
  ...GLOVES_BASES,
  ...BOOTS_BASES,
  ...BELT_BASES,
  ...WEAPON_BASES,
  ...OFFHAND_BASES,
  ...AMULET_BASES,
  ...RING_BASES,
  ...CAPE_BASES,
];

// Index by ID for fast lookup
const BASE_BY_ID = new Map<string, BaseItemDefinition>();
for (const base of ALL_BASES) {
  BASE_BY_ID.set(base.id, base);
}

// Index by slot for fast slot queries
const BASES_BY_SLOT = new Map<string, BaseItemDefinition[]>();
for (const base of ALL_BASES) {
  // Normalize ring slots: ring1 bases also apply to ring2
  const slotKey = base.slot === 'ring1' || base.slot === 'ring2' ? 'ring' : base.slot;
  const existing = BASES_BY_SLOT.get(slotKey) || [];
  existing.push(base);
  BASES_BY_SLOT.set(slotKey, existing);
}

// ========================
// Legacy base ID mapping (old EQUIPMENT_BASES keys -> new base IDs)
// ========================
export const LEGACY_BASE_MAP: Record<string, string> = {
  // Weapons
  'kilic': 'weapon_sword_1',
  'balta': 'weapon_axe_1',
  'yay': 'weapon_bow_1',
  'asa': 'weapon_staff_1',
  'uzun_kilic': 'weapon_sword_15',
  'savas_baltasi': 'weapon_axe_15',
  'kompozit_yay': 'weapon_bow_15',
  'kam_asasi': 'weapon_staff_15',
  // Chest
  'demir_zirh': 'chest_ar_1',
  'deri_zirh': 'chest_ev_1',
  'kumasli_kaftan': 'chest_es_1',
  'celik_zirh': 'chest_ar_15',
  'sert_deri': 'chest_ev_15',
  // Head
  'demir_tolgasi': 'head_ar_1',
  'börk': 'head_ev_1',
  'celik_tolga': 'head_ar_15',
  'avcı_sapkasi': 'head_ev_15',
  // Boots
  'cizme': 'boots_ev_1',
  'savas_cizmesi': 'boots_ar_15',
  'hafif_cizme': 'boots_ev_15',
  // Gloves
  'eldiven': 'gloves_ev_1',
  'savas_eldiveni': 'gloves_ar_15',
  'deri_eldiven': 'gloves_ev_1',
  // Belt
  'kemer': 'belt_ar_1',
  // Amulet
  'muska': 'amulet_1',
  // Ring
  'yuzuk': 'ring_1',
  // Capes
  'yun_pelerin': 'cape_ar_1',
  'ipek_pelerin': 'cape_ev_1',
  'savas_pelerini': 'cape_ar_15',

  // Set Items - Alp Er Tunga
  'set_alp_ertunga_kilic': 'weapon_sword_15',
  'set_alp_ertunga_demir_zirh': 'chest_ar_15',
  'set_alp_ertunga_demir_tolgasi': 'head_ar_15',
  'set_alp_ertunga_demir_eldiveni': 'gloves_ar_15',

  // Set Items - Asena
  'set_asena_silah': 'weapon_dagger_15',
  'set_asena_cizme': 'boots_ev_15',
  'set_asena_eldiven': 'gloves_ev_15',
  'set_asena_muska': 'amulet_15',

  // Set Items - Kam Bilgeligi
  'set_kam_bilgeligi_asa': 'weapon_staff_15',
  'set_kam_bilgeligi_cubbe': 'chest_es_15',
  'set_kam_bilgeligi_tolga': 'head_es_15',
  'set_kam_bilgeligi_yuzuk': 'ring_15',

  // Set Items - Göktürk
  'set_gokturk_silah': 'weapon_sword_15',
  'set_gokturk_zirh': 'chest_ar_30',
  'set_gokturk_cizme': 'boots_ar_15',
  'set_gokturk_kemer': 'belt_ar_30',

  // Set Items - Bozkır
  'set_bozkir_yay': 'weapon_bow_15',
  'set_bozkir_zirh': 'chest_ev_15',
  'set_bozkir_eldiven': 'gloves_ev_15',
  'set_bozkir_cizme': 'boots_ev_15',

  // Set Items - Ruh Yürüyücü
  'set_ruh_yuruyucu_muska': 'amulet_30',
  'set_ruh_yuruyucu_yuzuk': 'ring_30',
  'set_ruh_yuruyucu_pelerin': 'cape_ev_30',
};

// ========================
// Registry Functions
// ========================

/**
 * Get all bases for a given equipment slot.
 * For ring slots, returns all ring bases.
 */
export function getBasesForSlot(slot: EquipmentSlot): BaseItemDefinition[] {
  const slotKey = (slot === 'ring1' || slot === 'ring2') ? 'ring' : slot;
  return BASES_BY_SLOT.get(slotKey) || [];
}

/**
 * Get bases for a slot that are at or below a given level.
 * Picks the highest-level bases that qualify.
 */
export function getBasesForSlotAndLevel(slot: EquipmentSlot, maxLevel: number): BaseItemDefinition[] {
  return getBasesForSlot(slot).filter(base => base.level <= maxLevel);
}

/**
 * Get a specific base item definition by its ID.
 * Also checks the legacy mapping if not found directly.
 */
export function getBaseById(id: string): BaseItemDefinition | undefined {
  // Direct lookup
  const direct = BASE_BY_ID.get(id);
  if (direct) return direct;

  // Try legacy mapping
  const newId = LEGACY_BASE_MAP[id];
  if (newId) return BASE_BY_ID.get(newId);

  return undefined;
}

/**
 * Resolve a legacy base ID to the new base ID.
 */
export function resolveLegacyBaseId(legacyId: string): string {
  return LEGACY_BASE_MAP[legacyId] || legacyId;
}

/**
 * Get all base item definitions.
 */
export function getAllBases(): BaseItemDefinition[] {
  return ALL_BASES;
}

/**
 * Pick a random base from available bases for a slot at a given level.
 * Prefers bases closest to but not exceeding the given level.
 */
export function pickRandomBase(slot: EquipmentSlot, itemLevel: number): BaseItemDefinition | null {
  const available = getBasesForSlotAndLevel(slot, itemLevel);
  if (available.length === 0) return null;

  // Weight higher-level bases more heavily (they're more exciting to find)
  const weighted: { base: BaseItemDefinition; weight: number }[] = available.map(base => ({
    base,
    weight: 1 + base.level, // Simple linear weight
  }));

  const totalWeight = weighted.reduce((sum, w) => sum + w.weight, 0);
  let roll = Math.random() * totalWeight;

  for (const entry of weighted) {
    roll -= entry.weight;
    if (roll <= 0) return entry.base;
  }

  return available[available.length - 1];
}

/**
 * Get the default grid size for a slot (used for backward compatibility).
 */
export function getDefaultGridSize(slot: EquipmentSlot): GridSize {
  switch (slot) {
    case 'chest': return { width: 2, height: 3 };
    case 'weapon': return { width: 1, height: 3 };
    case 'offhand': return { width: 1, height: 3 };
    case 'gloves': return { width: 1, height: 2 };
    case 'boots': return { width: 1, height: 2 };
    case 'cape': return { width: 1, height: 2 };
    default: return { width: 1, height: 1 };
  }
}

/**
 * Get the EquipmentCategory for a given slot.
 */
export function getCategoryForSlot(slot: EquipmentSlot): EquipmentCategory {
  if (slot === 'weapon') return 'weapon';
  if (slot === 'amulet' || slot === 'ring1' || slot === 'ring2') return 'accessory';
  return 'armour';
}
